import { type ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';

import { taskTypeMeta } from '@/features/backlog/constants';

/**
 * Lightweight, theme-aware charts hand-rolled for the Reports page (the app ships no charting lib).
 * Chrome (axes/gridlines/labels) uses token classes so it flips with the theme; data series use the
 * shared categorical hex palette. All charts show a cursor-following tooltip on hover; the donut and
 * workload bars are click-through (a handler opens the matching search).
 */

export const ACCENT = '#2ec5e8';
export const VIOLET = '#9566ec';
export const typeColor = (t: string) => taskTypeMeta(t).color;

/** A cursor-following tooltip. Spread handlers(content) onto a hover target; render {portal} once. */
function useTooltip() {
  const [tip, setTip] = useState<{ x: number; y: number; content: ReactNode } | null>(null);
  const handlers = (content: ReactNode) => ({
    onMouseEnter: (e: React.MouseEvent) => setTip({ x: e.clientX, y: e.clientY, content }),
    onMouseMove: (e: React.MouseEvent) => setTip({ x: e.clientX, y: e.clientY, content }),
    onMouseLeave: () => setTip(null),
  });
  const portal = tip
    ? createPortal(
        <div
          className="pointer-events-none fixed z-[1000] whitespace-nowrap rounded-lg border border-line bg-surface-raised px-2.5 py-1.5 text-[11.5px] font-medium text-fg-soft shadow-xl shadow-black/30"
          style={{ left: Math.min(tip.x + 14, window.innerWidth - 200), top: tip.y + 16 }}
        >
          {tip.content}
        </div>,
        document.body,
      )
    : null;
  return { handlers, portal };
}

/** A premium KPI stat tile (optionally click-through). */
export function KpiTile({ label, value, sub, color, onClick }: { label: string; value: string; sub?: string; color?: string; onClick?: () => void }) {
  const body = (
    <>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-fg-faint">{label}</div>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="tnum text-[26px] font-semibold leading-none text-fg-strong" style={color ? { color } : undefined}>{value}</span>
        {sub ? <span className="text-[12px] text-fg-faint">{sub}</span> : null}
      </div>
    </>
  );
  const cls = 'rounded-2xl border border-line bg-surface-raised px-4 py-3.5 text-left transition-colors';
  return onClick ? (
    <button type="button" onClick={onClick} className={cn(cls, 'hover:border-accent/40 hover:bg-layer/40')} title="Open in search">{body}</button>
  ) : (
    <div className={cls}>{body}</div>
  );
}

type Bar = { label: string; value: number; title?: ReactNode };

/** Vertical bar chart (throughput). Hovering a column shows its tooltip; with onPick, a non-empty column
 *  is click-through (opens the matching search). */
export function BarChart({ data, color = ACCENT, height = 190, onPick }: { data: Bar[]; color?: string; height?: number; onPick?: (i: number) => void }) {
  const tt = useTooltip();
  const max = Math.max(1, ...data.map((d) => d.value));
  const plot = height - 20;
  return (
    <div>
      <div className="flex items-end gap-1.5" style={{ height }}>
        {data.map((d, i) => {
          const clickable = !!onPick && d.value > 0;
          return (
            <div
              key={i}
              onClick={clickable ? () => onPick!(i) : undefined}
              className={cn('flex min-w-0 flex-1 flex-col items-center justify-end gap-1', clickable && 'cursor-pointer')}
              title={clickable ? 'Open in search' : undefined}
              {...tt.handlers(d.title ?? `${d.label}: ${d.value}`)}
            >
              <span className="tnum text-[10px] font-medium text-fg-faint">{d.value || ''}</span>
              <div className={cn('w-full max-w-[40px] rounded-t-[4px] transition-[height,opacity] duration-300', clickable && 'hover:opacity-80')} style={{ height: `${(d.value / max) * plot}px`, minHeight: d.value > 0 ? 3 : 0, background: color }} />
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-1.5 border-t border-line pt-1.5">
        {data.map((d, i) => (
          <div key={i} className="min-w-0 flex-1 truncate text-center text-[10px] text-fg-ghost" title={d.label}>{d.label}</div>
        ))}
      </div>
      {tt.portal}
    </div>
  );
}

/** Area/line chart (cycle-time trend). Transparent hover columns show each point's tooltip; with onPick,
 *  a column with data is click-through. */
export function AreaChart({ data, color = VIOLET, height = 170, onPick }: { data: Bar[]; color?: string; height?: number; onPick?: (i: number) => void }) {
  const tt = useTooltip();
  const W = 620;
  const H = height;
  const padY = 14;
  const max = Math.max(1, ...data.map((d) => d.value));
  const n = data.length;
  const gx = (i: number) => (n <= 1 ? W / 2 : (i / (n - 1)) * W);
  const gy = (v: number) => H - padY - (v / max) * (H - padY * 2);
  const line = data.map((d, i) => `${gx(i).toFixed(1)},${gy(d.value).toFixed(1)}`).join(' ');
  const area = `${gx(0).toFixed(1)},${H} ${line} ${gx(n - 1).toFixed(1)},${H}`;
  const gid = 'rg-' + color.replace('#', '');
  const grid = [0, 0.5, 1];
  return (
    <div>
      <div className="relative w-full text-line" style={{ height }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full" role="img" aria-label="Average cycle time trend">
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.28" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>
          {grid.map((g, i) => (
            <line key={i} x1="0" x2={W} y1={gy(max * g)} y2={gy(max * g)} stroke="currentColor" strokeOpacity="0.5" strokeWidth="1" vectorEffect="non-scaling-stroke" strokeDasharray={i === 0 ? undefined : '3 4'} />
          ))}
          {n > 0 ? <polygon points={area} fill={`url(#${gid})`} /> : null}
          {n > 1 ? <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" /> : null}
        </svg>
        {/* Round HTML dots (unaffected by the SVG's non-uniform x-scaling). */}
        {data.map((d, i) => (
          <span key={i} className="pointer-events-none absolute h-[7px] w-[7px] -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface-raised" style={{ left: `${(gx(i) / W) * 100}%`, top: `${(gy(d.value) / H) * 100}%`, background: color }} />
        ))}
        {/* Transparent hover columns so the whole area is hoverable (and click-through) per point. */}
        <div className="absolute inset-0 flex">
          {data.map((d, i) => {
            const clickable = !!onPick && d.value > 0;
            return (
              <div
                key={i}
                onClick={clickable ? () => onPick!(i) : undefined}
                className={cn('h-full flex-1', clickable && 'cursor-pointer')}
                title={clickable ? 'Open in search' : undefined}
                {...tt.handlers(d.title ?? `${d.label}: ${d.value}`)}
              />
            );
          })}
        </div>
      </div>
      <div className="mt-1.5 flex gap-1.5 border-t border-line pt-1.5">
        {data.map((d, i) => (
          <div key={i} className="min-w-0 flex-1 truncate text-center text-[10px] text-fg-ghost" title={d.label}>{d.label}</div>
        ))}
      </div>
      {tt.portal}
    </div>
  );
}

/** Donut of the completed-by-type split; segments + legend are click-through. */
export function TypeDonut({ data, size = 132, onPick }: { data: { type: string; count: number }[]; size?: number; onPick?: (type: string) => void }) {
  const tt = useTooltip();
  const total = data.reduce((s, d) => s + d.count, 0);
  const R = 42;
  const C = 2 * Math.PI * R;
  let off = 0;
  const pct = (c: number) => (total ? Math.round((c / total) * 100) : 0);
  return (
    <div className="flex items-center gap-4">
      <svg viewBox="0 0 100 100" width={size} height={size} className="shrink-0 text-line" role="img" aria-label="Completed by type">
        <circle cx="50" cy="50" r={R} fill="none" stroke="currentColor" strokeOpacity="0.6" strokeWidth="13" />
        {total > 0 &&
          data.map((d) => {
            const len = (d.count / total) * C;
            const seg = (
              <circle
                key={d.type}
                cx="50"
                cy="50"
                r={R}
                fill="none"
                stroke={typeColor(d.type)}
                strokeWidth="13"
                strokeDasharray={`${len} ${C - len}`}
                strokeDashoffset={-off}
                transform="rotate(-90 50 50)"
                strokeLinecap="butt"
                style={onPick ? { cursor: 'pointer' } : undefined}
                onClick={onPick ? () => onPick(d.type) : undefined}
                {...tt.handlers(`${cap(d.type)} · ${d.count} (${pct(d.count)}%)`)}
              />
            );
            off += len;
            return seg;
          })}
        <text x="50" y="49" textAnchor="middle" className="fill-current text-fg-strong" style={{ fontSize: 20, fontWeight: 700 }}>{total}</text>
        <text x="50" y="63" textAnchor="middle" className="fill-current text-fg-faint" style={{ fontSize: 8, letterSpacing: 0.5 }}>DONE</text>
      </svg>
      <div className="min-w-0 space-y-1.5">
        {data.length === 0 ? (
          <div className="text-[12px] text-fg-faint">Nothing completed yet.</div>
        ) : (
          data.map((d) => (
            <button
              key={d.type}
              type="button"
              onClick={onPick ? () => onPick(d.type) : undefined}
              className={cn('flex w-full items-center gap-2 rounded-md px-1 py-0.5 text-left text-[12.5px]', onPick && 'transition-colors hover:bg-layer/60')}
              title={onPick ? 'Open in search' : undefined}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: typeColor(d.type) }} />
              <span className="capitalize text-fg-soft">{d.type}</span>
              <span className="tnum ml-auto text-fg-faint">{d.count}</span>
            </button>
          ))
        )}
      </div>
      {tt.portal}
    </div>
  );
}

const WL_SEGMENTS: { key: 'story' | 'task' | 'bug'; label: string }[] = [
  { key: 'story', label: 'Story' },
  { key: 'task', label: 'Task' },
  { key: 'bug', label: 'Bug' },
];

type WlRow = { assignee_id: string | null; name: string; active: boolean; story: number; task: number; bug: number; total: number };

/** Horizontal stacked bars of open work per assignee; each segment / the name is click-through. */
export function WorkloadBars({
  rows,
  renderLead,
  onPick,
}: {
  rows: WlRow[];
  renderLead?: (row: { assignee_id: string | null; name: string }) => ReactNode;
  onPick?: (sel: { assignee_id: string | null; name: string; type?: 'story' | 'task' | 'bug' }) => void;
}) {
  const tt = useTooltip();
  const max = Math.max(1, ...rows.map((r) => r.total));
  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        {WL_SEGMENTS.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5 text-[11.5px] text-fg-faint">
            <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: typeColor(s.key) }} />
            {s.label}
          </span>
        ))}
      </div>
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.assignee_id ?? 'none'} className="flex items-center gap-3">
            <button
              type="button"
              onClick={onPick ? () => onPick({ assignee_id: r.assignee_id, name: r.name }) : undefined}
              className={cn('flex w-[150px] shrink-0 items-center gap-2 truncate text-left text-[12.5px]', r.active ? 'text-fg-soft' : 'text-fg-faint', onPick && 'rounded hover:text-fg')}
              title={onPick ? `Open ${r.name}'s items in search` : undefined}
            >
              {renderLead ? renderLead(r) : null}
              <span className="truncate" title={r.name}>{r.name}</span>
            </button>
            <div className="flex h-5 flex-1 overflow-hidden rounded-md bg-layer">
              {WL_SEGMENTS.map((s) =>
                r[s.key] > 0 ? (
                  <div
                    key={s.key}
                    style={{ width: `${(r[s.key] / max) * 100}%`, background: typeColor(s.key), cursor: onPick ? 'pointer' : undefined }}
                    onClick={onPick ? () => onPick({ assignee_id: r.assignee_id, name: r.name, type: s.key }) : undefined}
                    {...tt.handlers(`${r.name} · ${s.label}: ${r[s.key]}`)}
                  />
                ) : null,
              )}
            </div>
            <div className="w-8 shrink-0 text-right tnum text-[13px] font-semibold text-fg-strong">{r.total}</div>
          </div>
        ))}
      </div>
      {tt.portal}
    </div>
  );
}

function cap(s: string) {
  return s ? s[0].toUpperCase() + s.slice(1) : s;
}
// local cn to avoid a cross-import cycle for this leaf file
function cn(...xs: (string | false | null | undefined)[]) {
  return xs.filter(Boolean).join(' ');
}
